import {Box, Flex, Icon, Image, showMessage, Text, Title} from 'exsportia-components'
import {DateTime} from 'luxon'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {GetBookingQueryReturnType} from '../../../api/booking/get-booking'
import {Button} from '../../../ui/components/button/button'
import {WBA_URL} from '../../../utils/environment'
import {useLocale} from '../../../utils/i18n'
import {useModalActions} from '../../../utils/modals'
import {WATCHES_IMAGE} from '../../../utils/remote-content'

type ActionsType = {
  icon?: string
  type: 'flat' | 'primary' | 'secondary'
  text: string
  onClick: () => void
}

type SplitPaymentExpireDateProps = {
  actions: ActionsType[]
  splitPaymentExpireDate?: number
}

export const SplitPaymentExpireDateInfo = ({actions, splitPaymentExpireDate}: SplitPaymentExpireDateProps) => {
  const {closeAllModals} = useModalActions()
  if (!splitPaymentExpireDate) {
    return null
  }
  const expireDate = splitPaymentExpireDate || 0

  const getTimeLeft = () => {
    const now = DateTime.now()
    const expireTime = DateTime.fromMillis(expireDate)
    const diff = expireTime.diff(now, ['days', 'hours', 'minutes', 'seconds']).toObject()
    if ((diff.seconds || 0) < 0) {
      closeAllModals()
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      }
    }

    return {
      days: Math.floor(diff.days || 0),
      hours: Math.floor(diff.hours || 0),
      minutes: Math.floor(diff.minutes || 0),
      seconds: Math.floor(diff.seconds || 0),
    }
  }
  const [timeLeft, setTimeLeft] = useState(getTimeLeft())

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft())
    }, 1000)

    return () => clearInterval(interval)
  }, [splitPaymentExpireDate])

  return (
    <Flex
      width='100%'
      mt='16px'
      flexDirection='column'
      gap='12px'
    >
      <Text settings={{fontSize: '12px'}}>Time left to pay this booking</Text>
      <Box
        width='100%'
        height='56px'
        py='8px'
        border='default-border'
        borderRadius='16px'
      >
        <Flex height='100%'>
          {timeLeft.days > 0 && (
            <Flex
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              height='100%'
              width='33%'
              borderRight='default-border'
            >
              <Text settings={{fontSize: '16px', fontWeight: 'bold'}}>{timeLeft.days}</Text>
              <Text settings={{fontSize: '12px'}}>Days</Text>
            </Flex>
          )}
          <Flex
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            height='100%'
            width='33%'
            borderRight='default-border'
          >
            <Text settings={{fontSize: '16px', fontWeight: 'bold'}}>{timeLeft.hours}</Text>
            <Text settings={{fontSize: '12px'}}>Hours</Text>
          </Flex>
          <Flex
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            height='100%'
            width='33%'
            borderRight='default-border'
          >
            <Text settings={{fontSize: '16px', fontWeight: 'bold'}}>{timeLeft.minutes}</Text>
            <Text settings={{fontSize: '12px'}}>Minutes</Text>
          </Flex>
          <Flex
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            height='100%'
            width='33%'
          >
            <Text settings={{fontSize: '16px', fontWeight: 'bold'}}>{timeLeft.seconds}</Text>
            <Text settings={{fontSize: '12px'}}>Seconds</Text>
          </Flex>
        </Flex>
      </Box>
      {actions.map((action, index) => (
        <Button
          key={index}
          icon={action.icon}
          type={action.type}
          onClick={action.onClick}
          settings={{icon: {m: '0 4px 0 0'}}}
        >
          {action.text}
        </Button>
      ))}
    </Flex>
  )
}

type SplitPaymentExpireDateHookProps = {
  handlePay: () => void
  splitPaymentExpireDate: number
  userId?: string
  isLoading: boolean
  booking: NonNullable<GetBookingQueryReturnType>
}

export const useSplitPaymentExpireDate = ({
  userId,
  isLoading,
  booking,
  handlePay,
  splitPaymentExpireDate,
}: SplitPaymentExpireDateHookProps) => {
  const {l} = useLocale()
  const [modalOpened, setModalOpened] = useState(false)
  const hasOpenedRef = useRef(false)
  const {openModal, closeModal} = useModalActions()
  const isSplitPaymentActive = useMemo(
    () => booking.splitPayment && !booking.isPaid && !booking.isCancelled,
    [booking.splitPayment, booking.isPaid, booking.isCancelled],
  )

  const modalData = useMemo(() => {
    const mainParticipant = booking.participants[0]
    const isMainParticipant = userId && (mainParticipant.userId === userId || booking.userId === userId)
    const title = isMainParticipant ? 'Partially Paid Booking' : 'Pay your part'
    const description = isMainParticipant
      ? 'All participants payments must be completed within the timer below or the booking will be auto cancelled'
      : 'A booking has been shared with you for you to pay your part, this booking will be auto cancelled if all participants have not paid within the timer below'
    let actionsToUse: ActionsType[] = [
      {
        type: 'flat',
        text: 'Go to booking',
        onClick: closeModal,
      },
    ]
    const payAction: ActionsType = {
      type: isMainParticipant ? 'flat' : 'primary',
      text: 'Pay now',
      onClick: () => {
        handlePay()
        closeModal()
      },
    }
    const shareAction: ActionsType = {
      icon: 'linked',
      type: isMainParticipant ? 'primary' : 'flat',
      text: isMainParticipant ? 'Share to Players' : 'Share',
      onClick: () => {
        void navigator.clipboard.writeText(`${WBA_URL}/${booking.venueId}/bookings/${booking.uid}`)
        showMessage(l('linkCopied'), 'success')
      },
    }
    if (isMainParticipant) {
      actionsToUse = [shareAction, payAction, ...actionsToUse]
    } else {
      actionsToUse = [payAction, shareAction, ...actionsToUse]
    }
    return {
      title,
      actions: actionsToUse,
      description,
    }
  }, [booking, userId, handlePay, closeModal])

  const splitPaymentExpireDateMarkup = useMemo(
    () =>
      isSplitPaymentActive ? (
        <SplitPaymentExpireDateInfo
          actions={modalData.actions}
          splitPaymentExpireDate={splitPaymentExpireDate}
        />
      ) : null,
    [isSplitPaymentActive, modalData.actions, splitPaymentExpireDate],
  )

  const openParticipantsModalHandler = useCallback(() => {
    if (!isSplitPaymentActive) {
      return
    }
    if (modalOpened || hasOpenedRef.current) {
      return
    }
    openModal({
      title: '',
      footerConfig: {
        disabled: true,
      },
      component: (
        <Flex
          mb='16px'
          flexDirection='column'
          gap='16px'
          alignItems='center'
        >
          <Icon
            settings={{
              width: '112px',
              height: '112px',
              borderRadius: '24px',
              bg: 'rgba(255, 158, 0, 0.1)',
              iconHeight: '64px',
              iconWidth: '64px',
            }}
            icon={
              <Image
                width={'64px'}
                height={'64px'}
                aspectRatio={1}
                size={'contain'}
                src={WATCHES_IMAGE}
              />
            }
          />
          <Title text={modalData.title} />
          <Text settings={{textAlign: 'center'}}>{modalData.description}</Text>
          {splitPaymentExpireDateMarkup}
        </Flex>
      ),
    })
    setModalOpened(true)
    hasOpenedRef.current = true
  }, [isSplitPaymentActive, modalOpened, openModal, modalData.title, modalData.description, splitPaymentExpireDateMarkup])

  useEffect(() => {
    const now = DateTime.now()
    const expireDate = DateTime.fromMillis(splitPaymentExpireDate)
    const isSplitPaymentBooking = isSplitPaymentActive && now.toMillis() < expireDate.toMillis()
    if (isLoading) {
      return
    }

    if (isSplitPaymentBooking && !modalOpened && !hasOpenedRef.current) {
      openParticipantsModalHandler()
    }
  }, [booking, openParticipantsModalHandler, isLoading, splitPaymentExpireDate, modalOpened, isSplitPaymentActive])

  return {
    componentMarkup: splitPaymentExpireDateMarkup,
  }
}
